import React, {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import type { Witness } from '../../store/booksStore'
import type { AnchoredRow } from '../../types/anchoredRowTypes'
import type { ChapterCharacterLayer } from '../../types/characterLayerTypes'
import type { ChapterSceneLayer } from '../../types/sceneTypes'
import type { ChapterStructuralRangeLayer } from '../../types/structuralRangeTypes'
import { STRUCTURAL_RANGE_KIND_LABELS } from '../../lib/structuralRangeTaxonomy'
import type {
  CameraLucidaEvent,
  CameraLucidaFinding,
  CameraLucidaPlateModel,
  CameraLucidaPersistedInquiry,
  CameraLucidaScope,
  CameraLucidaSectionKind,
} from '../../types/cameraLucidaTypes'
import type {
  PiMatrixAgentAgentReport,
  PiMatrixAgentAgentSession,
  PiMatrixAgentIdRemap,
  PiMatrixAgentModelOption,
  PiMatrixAgentThinkingLevel,
  PiMatrixAgentUnassignedFragment,
  PiMatrixAgentWindow,
} from '../../types/piMatrixAgent'
import {
  buildCameraLucidaPlateModel,
  cameraLucidaMatteRuns,
  cameraLucidaScopeRowsInVisibleLeaves,
  cameraLucidaVisibleWindow,
  cameraLucidaWitnessRuns,
  createCameraLucidaInitialState,
} from '../../lib/cameraLucidaModel'
import {
  cameraLucidaShownScope,
  reduceCameraLucidaState,
} from '../../lib/cameraLucidaReducer'
import { createCameraLucidaIdentityPresentation } from '../../lib/cameraLucidaPresentation'
import {
  CAMERA_LUCIDA_CAP_GAP,
  CAMERA_LUCIDA_STUB_BAND,
  CAMERA_LUCIDA_STUB_STEP,
  CAMERA_LUCIDA_TRAY_GAP,
  cameraLucidaRowOrdinalRange,
  computeCameraLucidaStubPresentation,
  computeCameraLucidaVerticalLayout,
} from '../../lib/cameraLucidaLayout'
import { useCameraLucidaInquiryHistory } from '../../hooks/useCameraLucidaInquiryHistory'
import './AgentCameraLucida.css'

const GROUP_GAP = 18
const STUB_TOP = 26
const LEAF_CHROME_HEIGHT = 54
const LEAF_FALLBACK_CHROME_HEIGHT = 40
const LEAF_TRANSITION_FALLBACK_MS = 760
const WIDE_REPORT_MEDIA_QUERY = '(min-width: 1780px)'

const DEFAULT_QUESTION = 'Which rows, witnesses, passage, or anomaly should the agent investigate?'

interface CameraLucidaInquiryContext {
  snapshotId?: string
  model: CameraLucidaPlateModel
  emit: (event: CameraLucidaEvent) => void
  signal: AbortSignal
}

export interface CameraLucidaPiAuthAttempt {
  providerName: string
  status: 'starting' | 'awaiting_browser' | 'awaiting_manual_code' | 'authenticated' | 'failed'
  authUrl?: string
  instructions?: string
  prompt?: string
  manualCodeSupported?: boolean
  error?: string
}

export interface CameraLucidaPiAuthController {
  isAuthenticated: boolean
  isBusy?: boolean
  error?: string | null
  attempt?: CameraLucidaPiAuthAttempt | null
  onLogin: () => void | Promise<void>
  onSubmitInput?: (value: string) => void | Promise<void>
}

export interface CameraLucidaAgentStartOptions {
  model?: { provider: string; modelId: string }
  thinkingLevel?: PiMatrixAgentThinkingLevel
  resumeAgentSessionId?: string
}

export interface CameraLucidaAgentController {
  current: PiMatrixAgentAgentSession | null
  configuredModel: { provider: string; modelId: string }
  configuredThinkingLevel: PiMatrixAgentThinkingLevel
  models: PiMatrixAgentModelOption[]
  report: PiMatrixAgentAgentReport | null
  isBusy?: boolean
  error?: string | null
  onRefresh: () => void | Promise<void>
  onStart: (options: CameraLucidaAgentStartOptions) => void | Promise<void>
}

export interface CameraLucidaProposalController {
  proposalId: string
  summary: string
  operation: string
  scope: CameraLucidaScope
  before?: PiMatrixAgentWindow
  after?: PiMatrixAgentWindow
  idRemap?: PiMatrixAgentIdRemap[]
  unassignedFragments?: PiMatrixAgentUnassignedFragment[]
  steps?: Array<{
    index: number
    summary: string
    operation: string
    scope: CameraLucidaScope
    before?: PiMatrixAgentWindow
    after?: PiMatrixAgentWindow
    idRemap?: PiMatrixAgentIdRemap[]
    unassignedFragments?: PiMatrixAgentUnassignedFragment[]
  }>
  isBusy?: boolean
  onApply: () => void | Promise<void>
  onDiscard: () => void
}

export interface AgentCameraLucidaViewProps {
  rows: AnchoredRow[]
  witnesses: Witness[]
  chapterId?: string
  characterLayer?: ChapterCharacterLayer
  sceneLayer?: ChapterSceneLayer
  structuralRangeLayer?: ChapterStructuralRangeLayer
  contextLabel?: string
  snapshotId?: string
  onClose?: () => void
  onInquiry?: (question: string, context: CameraLucidaInquiryContext) => Promise<void>
  piAuth?: CameraLucidaPiAuthController
  piAgent?: CameraLucidaAgentController
  piProposal?: CameraLucidaProposalController | null
}

interface CameraLayout {
  window: number[]
  positions: Record<number, number>
  capTop: number
  trayTop: number
  stubStep: number
  lift: number
}

interface MatteRect {
  top: number
  left: number
  width: number
  height: number
}

interface LeaderPath {
  id: string
  path: string
  active: boolean
}

type AgentRegisterTab = 'session' | 'inquiries' | 'agent-history' | 'tools'

const leaderPathListsEqual = (left: LeaderPath[], right: LeaderPath[]): boolean => (
  left.length === right.length && left.every((path, index) => (
    path.id === right[index]?.id
    && path.path === right[index]?.path
    && path.active === right[index]?.active
  ))
)

const matteCoordinateEqual = (left: number, right: number): boolean => (
  Object.is(left, right)
  || (Number.isFinite(left) && Number.isFinite(right) && Math.abs(left - right) < 0.25)
)

const matteRectsEqual = (left: MatteRect | null, right: MatteRect | null): boolean => (
  left === right || Boolean(left && right
    && matteCoordinateEqual(left.top, right.top)
    && matteCoordinateEqual(left.left, right.left)
    && matteCoordinateEqual(left.width, right.width)
    && matteCoordinateEqual(left.height, right.height))
)

const matteRectListsEqual = (
  left: Array<MatteRect | null>,
  right: Array<MatteRect | null>,
): boolean => left.length === right.length && left.every((rect, index) => matteRectsEqual(rect, right[index]))

const EMPTY_LAYOUT: CameraLayout = {
  window: [],
  positions: {},
  capTop: 0,
  trayTop: 0,
  stubStep: CAMERA_LUCIDA_STUB_STEP,
  lift: 0,
}

const useWideCameraLucidaReport = (): boolean => {
  const getMatches = () => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(WIDE_REPORT_MEDIA_QUERY).matches
  )
  const [matches, setMatches] = useState(getMatches)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const media = window.matchMedia(WIDE_REPORT_MEDIA_QUERY)
    const update = () => setMatches(media.matches)
    update()
    if (typeof media.addEventListener === 'function') media.addEventListener('change', update)
    else media.addListener(update)
    return () => {
      if (typeof media.removeEventListener === 'function') media.removeEventListener('change', update)
      else media.removeListener(update)
    }
  }, [])

  return matches
}

const roman = (value: number): string => {
  const pairs: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ]
  let rest = Math.max(1, value)
  let result = ''
  pairs.forEach(([amount, glyph]) => {
    while (rest >= amount) {
      result += glyph
      rest -= amount
    }
  })
  return result
}

const sectionKindLabel = (kind: CameraLucidaSectionKind): string => {
  if (kind === 'unclassified') return 'Passage'
  return STRUCTURAL_RANGE_KIND_LABELS[kind]
}

const cellBackground = (state: string, fill: number): string => {
  if (state === 'lacuna') {
    return 'repeating-linear-gradient(135deg,var(--occ-paper) 0,var(--occ-paper) 2px,var(--occ-paper-alt) 2px,var(--occ-paper-alt) 4px)'
  }
  if (state === 'not-carried') return 'var(--occ-paper-alt)'
  const stop = Math.round(Math.max(0, Math.min(1, fill)) * 100)
  return `linear-gradient(to bottom,var(--occ-ink) 0 ${stop}%,var(--occ-paper) ${stop}% 100%)`
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null
  return Boolean(element && (
    element.tagName === 'INPUT'
    || element.tagName === 'TEXTAREA'
    || element.tagName === 'SELECT'
    || element.isContentEditable
  ))
}

const classNames = (...values: Array<string | false | null | undefined>): string => values.filter(Boolean).join(' ')

const formatTokenCount = (value: number | null | undefined): string => {
  const count = Number(value) || 0
  if (count < 1000) return String(count)
  if (count < 1000000) return `${(count / 1000).toFixed(count < 10000 ? 1 : 0)}k`
  return `${(count / 1000000).toFixed(1)}m`
}

const formatSessionDate = (value: string): string => {
  if (!value) return 'date unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const AgentCameraLucidaView: React.FC<AgentCameraLucidaViewProps> = ({
  rows,
  witnesses,
  chapterId,
  characterLayer,
  sceneLayer,
  structuralRangeLayer,
  contextLabel = 'Collation',
  snapshotId,
  onClose,
  onInquiry,
  piAuth,
  piAgent,
  piProposal,
}) => {
  const model = useMemo(
    () => buildCameraLucidaPlateModel({
      rows,
      witnesses,
      characterLayer,
      sceneLayer,
      structuralLayer: structuralRangeLayer,
    }),
    [rows, witnesses, characterLayer, sceneLayer, structuralRangeLayer],
  )
  const [session, dispatch] = useReducer(reduceCameraLucidaState, undefined, createCameraLucidaInitialState)
  const [selectedLeaf, setSelectedLeaf] = useState(0)
  const [plateScale, setPlateScale] = useState(1)
  const [layout, setLayout] = useState<CameraLayout>(EMPTY_LAYOUT)
  const [risingLeaf, setRisingLeaf] = useState<number | null>(null)
  const [matteBlink, setMatteBlink] = useState(false)
  const [liveMattes, setLiveMattes] = useState<Array<MatteRect | null>>([null, null])
  const [citeMatte, setCiteMatte] = useState<MatteRect | null>(null)
  const [suggestMatte, setSuggestMatte] = useState<MatteRect | null>(null)
  const [leaderPaths, setLeaderPaths] = useState<LeaderPath[]>([])
  const [question, setQuestion] = useState('')
  const [isPiAuthOpen, setIsPiAuthOpen] = useState(false)
  const [piAuthInput, setPiAuthInput] = useState('')
  const [isPiAuthSubmitting, setIsPiAuthSubmitting] = useState(false)
  const [isAgentRegisterOpen, setIsAgentRegisterOpen] = useState(false)
  const [agentRegisterTab, setAgentRegisterTab] = useState<AgentRegisterTab>('session')
  const [restoredInquiry, setRestoredInquiry] = useState<CameraLucidaPersistedInquiry | null>(null)
  const [selectedAgentModelId, setSelectedAgentModelId] = useState(piAgent?.configuredModel.modelId || '')
  const [selectedThinkingLevel, setSelectedThinkingLevel] = useState<PiMatrixAgentThinkingLevel>(piAgent?.configuredThinkingLevel || 'medium')
  const [proposalScope, setProposalScope] = useState<CameraLucidaScope | null>(null)
  const [inspectedProposalId, setInspectedProposalId] = useState<string | null>(null)
  const wideReport = useWideCameraLucidaReport()
  const {
    inquiries: persistedInquiries,
    loading: inquiryHistoryLoading,
    error: inquiryHistoryError,
    refresh: refreshInquiryHistory,
  } = useCameraLucidaInquiryHistory(chapterId)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const stackRef = useRef<HTMLDivElement | null>(null)
  const capRef = useRef<HTMLDivElement | null>(null)
  const matteLayerRef = useRef<HTMLDivElement | null>(null)
  const plateRef = useRef<HTMLElement | null>(null)
  const reportRef = useRef<HTMLElement | null>(null)
  const discourseRef = useRef<HTMLDivElement | null>(null)
  const ledgerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const leafRefs = useRef(new Map<number, HTMLDivElement>())
  const rowRefs = useRef(new Map<string, HTMLDivElement>())
  const cellRefs = useRef(new Map<string, HTMLDivElement>())
  const inquiryAbortRef = useRef<AbortController | null>(null)
  const inquiryPendingRef = useRef(false)
  const previousChapterIdRef = useRef(chapterId)
  const previousSnapshotIdRef = useRef(snapshotId)
  const lastDrivenScopeKeyRef = useRef('')
  const streamPausedRef = useRef(false)
  const queuedEventsRef = useRef<CameraLucidaEvent[]>([])
  const liveMattesValueRef = useRef<Array<MatteRect | null>>([null, null])
  const citeMatteValueRef = useRef<MatteRect | null>(null)
  const suggestMatteValueRef = useRef<MatteRect | null>(null)
  const leaderPathsValueRef = useRef<LeaderPath[]>([])
  const settlingLeafRef = useRef<number | null>(null)
  const leafRiseFrameRef = useRef<number | null>(null)
  const leafSettleFrameRef = useRef<number | null>(null)
  const leafTransitionTimerRef = useRef<number | null>(null)

  const clearLeafTransitionSchedule = useCallback(() => {
    if (leafRiseFrameRef.current !== null) window.cancelAnimationFrame(leafRiseFrameRef.current)
    if (leafSettleFrameRef.current !== null) window.cancelAnimationFrame(leafSettleFrameRef.current)
    if (leafTransitionTimerRef.current !== null) window.clearTimeout(leafTransitionTimerRef.current)
    leafRiseFrameRef.current = null
    leafSettleFrameRef.current = null
    leafTransitionTimerRef.current = null
  }, [])

  const finishLeafTransition = useCallback((leafIndex: number) => {
    if (settlingLeafRef.current !== leafIndex) return
    settlingLeafRef.current = null
    clearLeafTransitionSchedule()
    setRisingLeaf(null)
    setMatteBlink(false)
  }, [clearLeafTransitionSchedule])

  useEffect(() => () => {
    settlingLeafRef.current = null
    clearLeafTransitionSchedule()
  }, [clearLeafTransitionSchedule])

  useEffect(() => {
    const previousChapterId = previousChapterIdRef.current
    const previousSnapshotId = previousSnapshotIdRef.current
    previousChapterIdRef.current = chapterId
    previousSnapshotIdRef.current = snapshotId
    const chapterChanged = previousChapterId !== chapterId
    const snapshotChanged = Boolean(previousSnapshotId && previousSnapshotId !== snapshotId)
    if (!chapterChanged && !snapshotChanged) return
    inquiryAbortRef.current?.abort()
    queuedEventsRef.current = []
    streamPausedRef.current = false
    settlingLeafRef.current = null
    clearLeafTransitionSchedule()
    setRisingLeaf(null)
    setMatteBlink(false)
    dispatch({ type: 'session.reset' })
    setRestoredInquiry(null)
    setSelectedLeaf(0)
  }, [chapterId, clearLeafTransitionSchedule, snapshotId])

  useEffect(() => {
    setSelectedLeaf(current => Math.min(current, Math.max(0, model.leaves.length - 1)))
  }, [model.leaves.length])

  useEffect(() => {
    const attemptStatus = piAuth?.attempt?.status
    if (attemptStatus === 'starting' || attemptStatus === 'awaiting_browser' || attemptStatus === 'awaiting_manual_code' || attemptStatus === 'failed') {
      setIsPiAuthOpen(true)
    }
    if (piAuth?.isAuthenticated) {
      setPiAuthInput('')
    }
  }, [piAuth?.attempt?.status, piAuth?.isAuthenticated])

  useEffect(() => {
    const configuredModelId = piAgent?.configuredModel.modelId || ''
    const configuredOption = piAgent?.models.find(option => option.modelId === configuredModelId)
    const selectedOption = configuredOption
      || piAgent?.models.find(option => option.available)
      || piAgent?.models[0]
    const configuredThinkingLevel = piAgent?.configuredThinkingLevel || 'medium'
    const availableLevels = selectedOption?.thinkingLevels || ['off']
    setSelectedAgentModelId(selectedOption?.modelId || configuredModelId)
    setSelectedThinkingLevel(
      availableLevels.includes(configuredThinkingLevel)
        ? configuredThinkingLevel
        : availableLevels.includes('medium')
          ? 'medium'
          : availableLevels[0] || 'off',
    )
  }, [piAgent?.configuredModel.modelId, piAgent?.configuredThinkingLevel, piAgent?.models])

  const allWitnessIds = useMemo(() => model.witnesses.map(witness => witness.id), [model.witnesses])
  const allWitnessIdSet = useMemo(() => new Set(allWitnessIds), [allWitnessIds])
  const rowById = useMemo(() => new Map(model.rows.map(row => [row.id, row])), [model.rows])
  const findingCoordinatesResolve = useCallback((finding: CameraLucidaFinding): boolean => (
    finding.scope.rowIds.length > 0
    && finding.scope.rowIds.every(rowId => rowById.has(rowId))
    && finding.scope.witnessIds.every(witnessId => allWitnessIdSet.has(witnessId))
  ), [allWitnessIdSet, rowById])
  const identityPresentation = useMemo(
    () => createCameraLucidaIdentityPresentation({ model, rows }),
    [model, rows],
  )
  const rowLeafIndex = useMemo(() => {
    const index = new Map<string, number>()
    model.leaves.forEach((leaf, leafIndex) => leaf.rows.forEach(row => index.set(row.id, leafIndex)))
    return index
  }, [model.leaves])
  const characterById = useMemo(() => new Map(model.characters.map(character => [character.id, character])), [model.characters])
  const presenceCharacterIdsByRowId = useMemo(() => {
    const index = new Map<string, string[]>()
    model.characterSpans
      .filter(span => span.kind === 'presence')
      .forEach(span => span.rowIds.forEach(rowId => {
        index.set(rowId, [...(index.get(rowId) || []), span.characterId])
      }))
    return index
  }, [model.characterSpans])
  const speakerSpansByLeafIndex = useMemo(() => {
    const index = new Map<number, CameraLucidaPlateModel['characterSpans']>()
    model.characterSpans
      .filter(span => span.kind === 'speaker')
      .forEach(span => {
        const leafIndices = new Set(
          span.rowIds
            .map(rowId => rowLeafIndex.get(rowId))
            .filter((leafIndex): leafIndex is number => leafIndex !== undefined)
        )
        leafIndices.forEach(leafIndex => {
          index.set(leafIndex, [...(index.get(leafIndex) || []), span])
        })
      })
    return index
  }, [model.characterSpans, rowLeafIndex])
  const shownScope = (
    piProposal && inspectedProposalId === piProposal.proposalId
      ? proposalScope
      : null
  ) || cameraLucidaShownScope(session)
  const citedFinding = useMemo(() => (
    session.findings.find(finding => finding.id === session.citedFindingId) || null
  ), [session.findings, session.citedFindingId])
  const reentryFinding = useMemo(() => (
    session.findings.find(finding => finding.id === session.reentryFindingId) || null
  ), [session.findings, session.reentryFindingId])
  const findings = useMemo(() => [
    ...session.findings.filter(finding => !finding.suggested),
    ...session.findings.filter(finding => finding.suggested),
  ], [session.findings])
  const summary = useMemo(() => (
    [...session.exchange].reverse().find(entry => entry.kind === 'summary')?.text || ''
  ), [session.exchange])

  useEffect(() => {
    setProposalScope(null)
    setInspectedProposalId(null)
  }, [session.citedFindingId, session.reentryFindingId])

  const emitStreamEvent = useCallback((event: CameraLucidaEvent) => {
    if (streamPausedRef.current && !['inquiry.failed'].includes(event.type)) {
      queuedEventsRef.current.push(event)
      return
    }
    dispatch(event)
  }, [])

  const resumeStream = useCallback(() => {
    streamPausedRef.current = false
    const queued = queuedEventsRef.current.splice(0)
    queued.forEach(event => dispatch(event))
  }, [])

  const leafForScope = useCallback((scope: CameraLucidaScope | null): number | null => {
    if (!scope?.rowIds.length) return null
    return rowLeafIndex.get(scope.rowIds[0]) ?? null
  }, [rowLeafIndex])

  const selectLeaf = useCallback((nextLeaf: number, rise = true) => {
    if (session.reentryFindingId || nextLeaf < 0 || nextLeaf >= model.leaves.length || nextLeaf === selectedLeaf) return
    const currentWindow = cameraLucidaVisibleWindow(model.leaves, selectedLeaf)
    if (session.citedFindingId) {
      const shouldResume = session.resumeAfterCitation
      dispatch({ type: 'finding.citation.cleared' })
      if (shouldResume) resumeStream()
    }
    settlingLeafRef.current = null
    clearLeafTransitionSchedule()
    if (rise && !currentWindow.includes(nextLeaf)) {
      settlingLeafRef.current = nextLeaf
      setRisingLeaf(nextLeaf)
      setMatteBlink(true)
      leafRiseFrameRef.current = window.requestAnimationFrame(() => {
        leafRiseFrameRef.current = null
        leafSettleFrameRef.current = window.requestAnimationFrame(() => {
          leafSettleFrameRef.current = null
          if (settlingLeafRef.current === nextLeaf) setRisingLeaf(null)
        })
      })
      leafTransitionTimerRef.current = window.setTimeout(
        () => finishLeafTransition(nextLeaf),
        LEAF_TRANSITION_FALLBACK_MS,
      )
    } else {
      setRisingLeaf(null)
      setMatteBlink(false)
    }
    setSelectedLeaf(nextLeaf)
  }, [clearLeafTransitionSchedule, finishLeafTransition, model.leaves, resumeStream, selectedLeaf, session.citedFindingId, session.reentryFindingId, session.resumeAfterCitation])

  useEffect(() => {
    if (!session.scope) {
      lastDrivenScopeKeyRef.current = ''
      return
    }
    const scopeKey = `${session.scope.rowIds.join('\u001f')}\u001e${session.scope.witnessIds.join('\u001f')}`
    if (!session.playing || scopeKey === lastDrivenScopeKeyRef.current) return
    lastDrivenScopeKeyRef.current = scopeKey
    const impliedLeaf = leafForScope(session.scope)
    if (impliedLeaf !== null && impliedLeaf !== selectedLeaf) selectLeaf(impliedLeaf)
  }, [leafForScope, selectLeaf, selectedLeaf, session.playing, session.scope])

  const computePlateScale = useCallback((): number => {
    const stageHeight = stackRef.current?.offsetHeight || 0
    if (!stageHeight || !model.leaves.length) return 1
    const captionLane = CAMERA_LUCIDA_CAP_GAP + 46 + CAMERA_LUCIDA_TRAY_GAP
    let nextScale = 1.6
    model.leaves.forEach((_, index) => {
      const visible = cameraLucidaVisibleWindow(model.leaves, index)
      let rowsPart = 0
      let rowCount = 0
      visible.forEach(leafIndex => model.leaves[leafIndex].rows.forEach(row => {
        rowsPart += row.height
        rowCount += 1
      }))
      const fixed = visible.length * LEAF_CHROME_HEIGHT + (visible.length - 1) * GROUP_GAP + captionLane + rowCount * 2
      const minimumTray = (model.leaves.length - visible.length - 1) * 10 + CAMERA_LUCIDA_STUB_BAND + 4
      if (rowsPart > 0) nextScale = Math.min(nextScale, (stageHeight - fixed - minimumTray) / rowsPart)
    })
    return Math.max(1, Math.min(1.6, nextScale))
  }, [model.leaves])

  const recomputeLayout = useCallback(() => {
    const stack = stackRef.current
    if (!stack) return
    const nextScale = computePlateScale()
    if (Math.abs(nextScale - plateScale) > 0.005) {
      setPlateScale(nextScale)
      return
    }
    const visible = cameraLucidaVisibleWindow(model.leaves, selectedLeaf)
    const positions: Record<number, number> = {}
    let y = 0
    visible.forEach(index => {
      positions[index] = y
      const fallback = LEAF_FALLBACK_CHROME_HEIGHT + model.leaves[index].rows.reduce((height, row) => height + row.height * plateScale + 2, 0)
      y += (leafRefs.current.get(index)?.offsetHeight || fallback) + GROUP_GAP
    })
    const windowBottom = Math.max(0, y - GROUP_GAP)
    const stageHeight = stack.offsetHeight || 4000
    const captionHeight = capRef.current?.offsetHeight || 46
    const stubCount = model.leaves.length - visible.length
    const { capTop, trayTop, stubStep, lift } = computeCameraLucidaVerticalLayout({
      stageHeight,
      captionHeight,
      windowBottom,
      stubCount,
    })
    visible.forEach(index => { positions[index] += lift })
    setLayout(previous => {
      const next = { window: visible, positions, capTop, trayTop, stubStep, lift }
      return JSON.stringify(previous) === JSON.stringify(next) ? previous : next
    })
  }, [computePlateScale, model.leaves, plateScale, selectedLeaf])

  useLayoutEffect(() => {
    recomputeLayout()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(recomputeLayout)
    if (stackRef.current) observer.observe(stackRef.current)
    if (capRef.current) observer.observe(capRef.current)
    leafRefs.current.forEach(element => observer.observe(element))
    return () => observer.disconnect()
  }, [recomputeLayout])

  const makeMatteRect = useCallback((scope: CameraLucidaScope, startColumn: number, endColumn: number): MatteRect | null => {
    const layer = matteLayerRef.current
    const orderedRows = cameraLucidaScopeRowsInVisibleLeaves(scope.rowIds, rowLeafIndex, layout.window)
      .map(rowId => rowById.get(rowId))
      .filter((row): row is NonNullable<typeof row> => Boolean(row))
      .sort((left, right) => left.ordinal - right.ordinal)
    const firstRow = orderedRows[0]
    const lastRow = orderedRows[orderedRows.length - 1]
    const firstWitness = model.witnesses[startColumn]
    const lastWitness = model.witnesses[endColumn]
    if (!layer || !firstRow || !lastRow || !firstWitness || !lastWitness) return null
    const topElement = rowRefs.current.get(firstRow.id)
    const bottomElement = rowRefs.current.get(lastRow.id)
    const leftElement = cellRefs.current.get(`${firstRow.id}:${firstWitness.id}`)
    const rightElement = cellRefs.current.get(`${firstRow.id}:${lastWitness.id}`)
    if (!topElement || !bottomElement || !leftElement || !rightElement) return null
    const layerRect = layer.getBoundingClientRect()
    const topRect = topElement.getBoundingClientRect()
    const bottomRect = bottomElement.getBoundingClientRect()
    const leftRect = leftElement.getBoundingClientRect()
    const rightRect = rightElement.getBoundingClientRect()
    return {
      top: topRect.top - layerRect.top,
      left: leftRect.left - layerRect.left,
      width: rightRect.right - leftRect.left,
      height: bottomRect.bottom - topRect.top,
    }
  }, [layout.window, model.witnesses, rowById, rowLeafIndex])

  useLayoutEffect(() => {
    const focused = shownScope && leafForScope(shownScope) === selectedLeaf
    const citing = Boolean(session.citedFindingId && !session.reentryFindingId)
    let nextLiveMattes: Array<MatteRect | null> = [null, null]
    let nextCiteMatte: MatteRect | null = null
    let nextSuggestMatte: MatteRect | null = null
    if (matteBlink || !shownScope || !focused) {
      nextLiveMattes = [null, null]
    } else {
      const runs = cameraLucidaMatteRuns(allWitnessIds, shownScope.witnessIds)
      if (citing && runs.length) {
        nextCiteMatte = makeMatteRect(shownScope, runs[0][0], runs[runs.length - 1][1])
      } else {
        nextLiveMattes = [0, 1].map(index => {
          const run = runs[index]
          return run ? makeMatteRect(shownScope, run[0], run[1]) : null
        })
      }
    }
    if (!matteBlink && session.suggestion && !session.reentryFindingId && session.citedFindingId !== session.findings.find(finding => finding.suggested)?.id
      && leafForScope(session.suggestion) === selectedLeaf) {
      const runs = cameraLucidaWitnessRuns(allWitnessIds, session.suggestion.witnessIds)
      nextSuggestMatte = runs.length ? makeMatteRect(session.suggestion, runs[0][0], runs[runs.length - 1][1]) : null
    }
    if (!matteRectListsEqual(liveMattesValueRef.current, nextLiveMattes)) {
      liveMattesValueRef.current = nextLiveMattes
      setLiveMattes(nextLiveMattes)
    }
    if (!matteRectsEqual(citeMatteValueRef.current, nextCiteMatte)) {
      citeMatteValueRef.current = nextCiteMatte
      setCiteMatte(nextCiteMatte)
    }
    if (!matteRectsEqual(suggestMatteValueRef.current, nextSuggestMatte)) {
      suggestMatteValueRef.current = nextSuggestMatte
      setSuggestMatte(nextSuggestMatte)
    }
  }, [allWitnessIds, layout, leafForScope, makeMatteRect, matteBlink, plateScale, selectedLeaf, session.citedFindingId, session.findings, session.reentryFindingId, session.suggestion, shownScope])

  const rowRangeLabel = useCallback((scope: CameraLucidaScope): string => {
    const ordinals = scope.rowIds.map(rowId => rowById.get(rowId)?.ordinal).filter((value): value is number => typeof value === 'number')
    const range = cameraLucidaRowOrdinalRange(ordinals)
    return range ? `${range.first}–${range.last}` : 'unresolved'
  }, [rowById])

  const findingPlate = useCallback((finding: CameraLucidaFinding): { index: number; lastIndex: number; title: string } => {
    const leafIndexes = finding.scope.rowIds
      .map(rowId => rowLeafIndex.get(rowId))
      .filter((index): index is number => index !== undefined)
      .filter((index, position, indexes) => indexes.indexOf(index) === position)
      .sort((left, right) => left - right)
    const index = leafIndexes[0] ?? 0
    return {
      index,
      lastIndex: leafIndexes[leafIndexes.length - 1] ?? index,
      title: model.leaves[index]?.title || contextLabel,
    }
  }, [contextLabel, model.leaves, rowLeafIndex])

  const plateNumerals = useCallback((plate: ReturnType<typeof findingPlate>): string => (
    plate.index === plate.lastIndex
      ? roman(plate.index + 1)
      : `${roman(plate.index + 1)}–${roman(plate.lastIndex + 1)}`
  ), [])

  const citeFinding = useCallback((findingId: string) => {
    const finding = session.findings.find(candidate => candidate.id === findingId)
    if (!finding) return
    const impliedLeaf = leafForScope(finding.scope)
    streamPausedRef.current = true
    dispatch({ type: 'finding.cited', findingId })
    if (impliedLeaf !== null && impliedLeaf !== selectedLeaf) {
      const currentWindow = cameraLucidaVisibleWindow(model.leaves, selectedLeaf)
      if (!currentWindow.includes(impliedLeaf)) {
        setRisingLeaf(impliedLeaf)
        setMatteBlink(true)
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => setRisingLeaf(null)))
        window.setTimeout(() => setMatteBlink(false), 620)
      }
      setSelectedLeaf(impliedLeaf)
    }
  }, [leafForScope, model.leaves, selectedLeaf, session.findings])

  const clearCitation = useCallback(() => {
    if (!session.citedFindingId || session.reentryFindingId) return
    const shouldResume = session.resumeAfterCitation
    dispatch({ type: 'finding.citation.cleared' })
    if (shouldResume) resumeStream()
    else streamPausedRef.current = false
  }, [resumeStream, session.citedFindingId, session.reentryFindingId, session.resumeAfterCitation])

  const enterFinding = useCallback((findingId: string) => {
    if (!session.findings.some(finding => finding.id === findingId)) return
    streamPausedRef.current = true
    dispatch({ type: 'finding.entered', findingId })
  }, [session.findings])

  const exitFinding = useCallback(() => {
    if (session.reentryFindingId) dispatch({ type: 'finding.exited' })
  }, [session.reentryFindingId])

  const resetSession = useCallback(() => {
    inquiryAbortRef.current?.abort()
    inquiryAbortRef.current = null
    inquiryPendingRef.current = false
    queuedEventsRef.current = []
    streamPausedRef.current = false
    dispatch({ type: 'session.reset' })
    setRestoredInquiry(null)
    setSelectedLeaf(0)
    setQuestion('')
    setProposalScope(null)
    setInspectedProposalId(null)
  }, [])

  const inspectPersistedInquiry = useCallback((inquiry: CameraLucidaPersistedInquiry) => {
    if (piAgent?.isBusy || !['standing by', 'idle', 'error'].includes(session.status)) return
    inquiryAbortRef.current?.abort()
    inquiryAbortRef.current = null
    inquiryPendingRef.current = false
    queuedEventsRef.current = []
    streamPausedRef.current = false
    dispatch({ type: 'inquiry.restored', inquiry })
    setRestoredInquiry(inquiry)
    setQuestion('')
    setProposalScope(null)
    setInspectedProposalId(null)
    const firstResolvedLeaf = inquiry.findings
      .map(finding => leafForScope(finding.scope))
      .find((leafIndex): leafIndex is number => leafIndex !== null)
    setSelectedLeaf(firstResolvedLeaf ?? 0)
    setIsAgentRegisterOpen(false)
  }, [leafForScope, piAgent?.isBusy, session.status])

  const sendQuestion = useCallback(async () => {
    if (piAgent?.isBusy || inquiryPendingRef.current || !['standing by', 'idle', 'error'].includes(session.status)) return
    const text = question.trim() || inputRef.current?.placeholder || DEFAULT_QUESTION
    const controller = new AbortController()
    inquiryAbortRef.current?.abort()
    inquiryAbortRef.current = controller
    inquiryPendingRef.current = true
    queuedEventsRef.current = []
    streamPausedRef.current = false
    // Reset the previous review at the inquiry boundary. Clearing from a
    // session-scope effect races with the final streamed read event and can
    // collapse a proposal immediately after the scholar clicks “inspect”.
    setProposalScope(null)
    setInspectedProposalId(null)
    setRestoredInquiry(null)
    dispatch({ type: 'inquiry.opened', question: text, snapshotId })
    setSelectedLeaf(0)
    setQuestion('')
    inputRef.current?.blur()
    if (!onInquiry) {
      dispatch({ type: 'inquiry.failed', message: 'Camera Lucida’s inquiry stream is not connected.' })
      inquiryPendingRef.current = false
      inquiryAbortRef.current = null
      return
    }
    try {
      const emitCurrentEvent = (event: CameraLucidaEvent) => {
        if (!controller.signal.aborted && inquiryAbortRef.current === controller) {
          emitStreamEvent(event)
        }
      }
      await onInquiry(text, { snapshotId, model, emit: emitCurrentEvent, signal: controller.signal })
      await refreshInquiryHistory()
    } catch (error) {
      if (controller.signal.aborted) return
      emitStreamEvent({ type: 'inquiry.failed', message: error instanceof Error ? error.message : 'The inquiry failed.' })
    } finally {
      if (inquiryAbortRef.current === controller) {
        inquiryPendingRef.current = false
        inquiryAbortRef.current = null
      }
    }
  }, [emitStreamEvent, model, onInquiry, piAgent?.isBusy, question, refreshInquiryHistory, session.status, snapshotId])

  useEffect(() => () => inquiryAbortRef.current?.abort(), [])

  const pauseToggle = useCallback(() => {
    if (session.status === 'idle' || session.status === 'standing by' || session.status === 'error') return
    if (session.playing) {
      streamPausedRef.current = true
      dispatch({ type: 'inquiry.paused' })
    } else if (session.status === 'paused') {
      dispatch({ type: 'inquiry.resumed' })
      resumeStream()
    }
  }, [resumeStream, session.playing, session.status])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return
      if (event.key === 'Escape') {
        if (isAgentRegisterOpen) setIsAgentRegisterOpen(false)
        else if (isPiAuthOpen) setIsPiAuthOpen(false)
        else if (session.reentryFindingId) exitFinding()
        else if (session.citedFindingId) clearCitation()
        else onClose?.()
        return
      }
      if (event.key === ' ') {
        event.preventDefault()
        pauseToggle()
        return
      }
      if (event.key === 'Enter') {
        if (session.reentryFindingId) return
        if (session.citedFindingId) enterFinding(session.citedFindingId)
        else void sendQuestion()
        return
      }
      if (event.key === 'ArrowLeft') {
        selectLeaf(selectedLeaf - 1)
        return
      }
      if (event.key === 'ArrowRight') {
        selectLeaf(selectedLeaf + 1)
        return
      }
      if (/^[1-9]$/.test(event.key) && !session.reentryFindingId) {
        const finding = findings[Number(event.key) - 1]
        if (finding) citeFinding(finding.id)
        return
      }
      if (event.key === 'r' || event.key === 'R') resetSession()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [citeFinding, clearCitation, enterFinding, exitFinding, findings, isAgentRegisterOpen, isPiAuthOpen, onClose, pauseToggle, resetSession, selectLeaf, selectedLeaf, sendQuestion, session.citedFindingId, session.reentryFindingId])

  useEffect(() => {
    const discourse = discourseRef.current
    if (discourse) discourse.scrollTop = discourse.scrollHeight
  }, [session.exchange, session.live])

  useLayoutEffect(() => {
    const ledger = ledgerRef.current
    if (!ledger || !reentryFinding) return
    const columns = reentryFinding.scope.witnessIds.map(id => allWitnessIds.indexOf(id)).filter(index => index >= 0)
    const first = columns.length ? Math.min(...columns) : -1
    ledger.scrollTop = 0
    ledger.scrollLeft = first >= 0 ? Math.max(0, 84 + first * 180 - 260) : 0
  }, [allWitnessIds, reentryFinding])

  const commitLeaderPaths = useCallback((paths: LeaderPath[]) => {
    if (leaderPathListsEqual(leaderPathsValueRef.current, paths)) return
    leaderPathsValueRef.current = paths
    setLeaderPaths(paths)
  }, [])

  const drawLeaders = useCallback(() => {
    const report = reportRef.current
    const plate = plateRef.current
    if (!report || !plate || !report.offsetParent) {
      commitLeaderPaths([])
      return
    }
    const plateRect = plate.getBoundingClientRect()
    const paths = findings.flatMap(finding => {
      const card = Array.from(report.querySelectorAll<HTMLElement>('[data-finding-id]'))
        .find(candidate => candidate.dataset.findingId === finding.id)
      const leafIndex = leafForScope(finding.scope)
      const leaf = leafIndex === null ? null : leafRefs.current.get(leafIndex)
      const target = leaf?.classList.contains('stub')
        ? leaf.querySelector<HTMLElement>('.stubface')
        : leaf?.querySelector<HTMLElement>('.leaf')
      if (!card || !target) return []
      const cardRect = card.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      if (cardRect.top < plateRect.top + 4 || cardRect.top > plateRect.bottom - 8) return []
      const x1 = cardRect.left - plateRect.left - 6
      const y1 = cardRect.top - plateRect.top + 13
      const x2 = targetRect.right - plateRect.left + 2
      const y2 = targetRect.top - plateRect.top + targetRect.height / 2
      return [{
        id: finding.id,
        path: `M ${x1} ${y1} L ${x1 - 14} ${y1} L ${x2} ${y2}`,
        active: session.citedFindingId === finding.id,
      }]
    })
    commitLeaderPaths(paths)
  }, [commitLeaderPaths, findings, leafForScope, session.citedFindingId])

  useLayoutEffect(() => {
    drawLeaders()
    const timer = window.setTimeout(drawLeaders, 700)
    return () => window.clearTimeout(timer)
  }, [drawLeaders, layout])

  const pageStyles = useCallback((leafIndex: number): CSSProperties => {
    if (risingLeaf === leafIndex) {
      return { transform: 'translateY(82vh)', clipPath: 'inset(0 0 0 0)', zIndex: 50 }
    }
    if (layout.window.includes(leafIndex)) {
      return {
        zIndex: leafIndex === selectedLeaf ? 45 : 40,
        clipPath: 'inset(0 0 0 0)',
        transform: `translateY(${layout.positions[leafIndex] || 0}px)`,
      }
    }
    const stubIndices = model.leaves.map((_, index) => index).filter(index => !layout.window.includes(index))
    const stubIndex = stubIndices.indexOf(leafIndex)
    const depth = stubIndices.length - 1 - stubIndex
    const stubPresentation = computeCameraLucidaStubPresentation(depth)
    return {
      zIndex: 10 + stubIndex,
      clipPath: `inset(${STUB_TOP}px 0 calc(100% - ${STUB_TOP + CAMERA_LUCIDA_STUB_BAND}px) 0)`,
      transform: `translateY(${layout.trayTop + stubIndex * layout.stubStep - STUB_TOP}px) scale(${stubPresentation.scale.toFixed(3)})`,
      filter: `brightness(${stubPresentation.brightness.toFixed(3)})`,
    }
  }, [layout, model.leaves, risingLeaf, selectedLeaf])

  const activityMain = identityPresentation.safeAgentText(citedFinding && !reentryFinding
    ? (() => {
      const plate = findingPlate(citedFinding)
      return `CITED · PLATE ${plateNumerals(plate)} · ROWS ${rowRangeLabel(citedFinding.scope)} · ⏎ RE-ENTER · ESC RESUME`
    })()
    : session.activity.main)
  const activityUnits = identityPresentation.safeAgentText(citedFinding && !reentryFinding
    ? citedFinding.scope.rowIds.map(rowId => rowById.get(rowId)?.title || identityPresentation.rowLabel(rowId)).filter(Boolean).join(' · ')
    : session.activity.units)
  const activeScopeLeaf = leafForScope(shownScope)
  const scopeRowIds = new Set(shownScope?.rowIds || [])
  const scopeWitnessIds = new Set(shownScope?.witnessIds || [])
  const focused = Boolean(shownScope && activeScopeLeaf === selectedLeaf)
  const statusLive = session.status !== 'idle' && session.status !== 'standing by' && session.status !== 'error'
  const agentOccupied = Boolean(piAgent?.isBusy && !statusLive)
  const statusLabel = agentOccupied ? 'agent busy' : restoredInquiry ? 'archived report' : session.status
  const overlayCharacter = session.overlay?.characterId ? characterById.get(session.overlay.characterId) : null

  const RichText: React.FC<{ text: string; scholarAuthored?: boolean }> = ({ text, scholarAuthored = false }) => {
    const displayText = scholarAuthored ? text : identityPresentation.safeAgentText(text)
    const tokens = displayText.split(/(\[[A-Za-z0-9_-]+\])/g)
    return <>{tokens.map((token, index): ReactNode => {
      const id = /^\[([A-Za-z0-9_-]+)\]$/.exec(token)?.[1]
      const finding = id ? session.findings.find(candidate => candidate.id === id) : null
      if (!finding) return <React.Fragment key={`${token}-${index}`}>{token}</React.Fragment>
      const coordinatesResolve = findingCoordinatesResolve(finding)
      if (!coordinatesResolve) {
        return (
          <span
            className={classNames('citeref', 'unresolved', finding.suggested && 'suggest')}
            key={`${finding.id}-${index}`}
            title="The cited rows or witnesses do not exist in the current collation snapshot."
          >
            {`⟨${finding.id} · coordinates unavailable⟩`}
          </span>
        )
      }
      const plate = findingPlate(finding)
      return (
        <span
          className={classNames('citeref', finding.suggested && 'suggest')}
          key={`${finding.id}-${index}`}
          onClick={() => citeFinding(finding.id)}
          onKeyDown={event => { if (event.key === 'Enter') citeFinding(finding.id) }}
          role="button"
          tabIndex={0}
        >
          {`⟨pl ${plateNumerals(plate)} · ${rowRangeLabel(finding.scope)}⟩`}
        </span>
      )
    })}</>
  }

  const inspectProposal = () => {
    if (!piProposal) return
    const impliedLeaf = leafForScope(piProposal.scope)
    setProposalScope(piProposal.scope)
    setInspectedProposalId(piProposal.proposalId)
    if (impliedLeaf !== null && impliedLeaf !== selectedLeaf) selectLeaf(impliedLeaf)
  }

  const proposalReviewSteps = useMemo(() => {
    if (!piProposal) return []
    const steps = piProposal.steps?.length ? piProposal.steps : [{
      index: 1,
      summary: piProposal.summary,
      operation: piProposal.operation,
      scope: piProposal.scope,
      before: piProposal.before,
      after: piProposal.after,
      idRemap: piProposal.idRemap,
      unassignedFragments: piProposal.unassignedFragments,
    }]
    return steps.map(step => {
      const afterRows = new Map(step.after?.rows.map(row => [row.id, row]) || [])
      const evidence = step.before?.rows.flatMap(beforeRow => {
        const afterRow = afterRows.get(beforeRow.id)
        const witnessIds = Array.from(new Set([
          ...beforeRow.cells.map(cell => cell.witnessId),
          ...(afterRow?.cells.map(cell => cell.witnessId) || []),
        ]))
        return witnessIds.map(witnessId => ({
          key: `${step.index}:${beforeRow.id}:${witnessId}`,
          label: beforeRow.label,
          witness: step.before?.witnesses.find(candidate => candidate.id === witnessId)?.sigla || identityPresentation.witnessLabel(witnessId),
          before: beforeRow.cells.find(cell => cell.witnessId === witnessId),
          after: afterRow?.cells.find(cell => cell.witnessId === witnessId),
        }))
      }) || []
      const rowLabel = (rowId: string): string => (
        step.before?.rows.find(row => row.id === rowId)?.label
        || step.after?.rows.find(row => row.id === rowId)?.label
        || identityPresentation.rowLabel(rowId)
      )
      const witnessLabel = (witnessId: string): string => (
        step.before?.witnesses.find(witness => witness.id === witnessId)?.sigla
        || step.after?.witnesses.find(witness => witness.id === witnessId)?.sigla
        || identityPresentation.witnessLabel(witnessId)
      )
      return { ...step, evidence, rowLabel, witnessLabel }
    })
  }, [identityPresentation, piProposal])
  const proposalInspected = Boolean(piProposal && inspectedProposalId === piProposal.proposalId)
  const proposalHasEvidence = Boolean(
    proposalReviewSteps.length
    && proposalReviewSteps.every(step => (
      step.before
      && step.after
      && step.evidence.length
      && (!step.operation.includes('split') || step.unassignedFragments?.length)
    ))
  )

  const FindingCards: React.FC<{ report?: boolean; showProposal?: boolean }> = ({ report = false, showProposal = false }) => (
    <>
      {report && <div className="repohead">report of the inquiry</div>}
      {report && summary && <div className="reposum"><RichText text={summary} /></div>}
      {!findings.length && !(showProposal && piProposal && !restoredInquiry) && <div className="dempty">{restoredInquiry
        ? 'This archived inquiry contains no filed findings.'
        : 'Findings will file here as the agent reads.'}</div>}
      {findings.map((finding, index) => {
        const plate = findingPlate(finding)
        const coordinatesResolve = findingCoordinatesResolve(finding)
        return (
          <div
            aria-disabled={!coordinatesResolve}
            className={classNames('dcard', finding.suggested && 'suggest', !coordinatesResolve && 'unresolved', session.citedFindingId === finding.id && 'active')}
            data-finding-id={finding.id}
            key={finding.id}
            onClick={() => { if (coordinatesResolve) citeFinding(finding.id) }}
            onKeyDown={event => { if (coordinatesResolve && event.key === 'Enter') citeFinding(finding.id) }}
            role="button"
            tabIndex={coordinatesResolve ? 0 : -1}
            title={coordinatesResolve ? undefined : 'The cited coordinates do not exist in the current collation snapshot.'}
          >
            <span className={`dk k-${finding.kind}`}>{finding.kind}</span>
            <span className="dn">{index + 1}</span>
            <span className="dt">{identityPresentation.safeAgentText(finding.title)}</span>
            <span className="dc">{coordinatesResolve
              ? `pl ${plateNumerals(plate)} · rows ${rowRangeLabel(finding.scope)} ↗`
              : restoredInquiry ? 'archived coordinates unavailable' : `rows ${rowRangeLabel(finding.scope)}`}</span>
          </div>
        )
      })}
      {showProposal && piProposal && !restoredInquiry && (
        <section className="dcard proposal" aria-label="Pending Pi edit proposal">
          <span className="dk">approval required</span>
          <span className="dn">π</span>
          <span className="dt">{identityPresentation.safeAgentText(piProposal.summary || 'Pi edit proposal')}</span>
          <span className="dc">{`${piProposal.operation.replace(/_/g, ' ')} · rows ${rowRangeLabel(piProposal.scope)}`}</span>
          {proposalInspected && (
            proposalHasEvidence ? (
              <div className="dpreview" role="region" aria-label="Proposal before and after">
                {proposalReviewSteps.map(step => (
                  <div className="dpreview-step" key={step.index}>
                    {proposalReviewSteps.length > 1 && (
                      <div className="dpreview-step-title">
                        {`edit ${step.index} · ${step.operation.replace(/_/g, ' ')} · ${identityPresentation.safeAgentText(step.summary)}`}
                      </div>
                    )}
                    <div className="dpreview-head"><span>before</span><span>after</span></div>
                    {step.evidence.map(evidence => (
                      <div className="dpreview-row" key={evidence.key}>
                        {[evidence.before, evidence.after].map((cell, index) => (
                          <div className="dpreview-cell" key={`${evidence.key}:${index}`}>
                            <span>{`${evidence.label} · ${evidence.witness} · ${cell?.state || 'gap'}`}</span>
                            <bdi dir="auto">{cell?.text || (cell?.state === 'excluded' ? '⟨deliberate gap⟩' : '⟨gap⟩')}</bdi>
                          </div>
                        ))}
                      </div>
                    ))}
                    {step.unassignedFragments?.map(fragment => (
                      <div className="dpreview-disclosure" key={fragment.fragmentId}>
                        <span>{`unassigned fragment · ${step.witnessLabel(fragment.sourceWitnessId)} · ${step.rowLabel(fragment.sourceRowId)}`}</span>
                        <bdi dir="auto">{fragment.text || '⟨empty fragment⟩'}</bdi>
                      </div>
                    ))}
                    {Boolean(step.idRemap?.length) && (
                      <div className="dpreview-remap">
                        {`${step.idRemap?.length} unit ${step.idRemap?.length === 1 ? 'identity' : 'identities'} consolidated into the surviving reading`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : <div className="dpreview-unavailable">Preview evidence unavailable; apply is disabled.</div>
          )}
          <div className="dactions">
            <button type="button" aria-expanded={proposalInspected} onClick={inspectProposal} disabled={piProposal.isBusy}>inspect</button>
            <button type="button" className="approve" onClick={() => void piProposal.onApply()} disabled={piProposal.isBusy || !proposalHasEvidence || !proposalInspected}>apply</button>
            <button type="button" onClick={piProposal.onDiscard} disabled={piProposal.isBusy}>discard</button>
          </div>
        </section>
      )}
    </>
  )

  const onAskSubmit = (event: FormEvent) => {
    event.preventDefault()
    void sendQuestion()
  }

  const onAskKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      void sendQuestion()
    } else if (event.key === 'Escape') {
      inputRef.current?.blur()
    }
    event.stopPropagation()
  }

  const startPiLogin = () => {
    if (!piAuth || piAuth.isBusy) return
    setIsAgentRegisterOpen(false)
    setIsPiAuthOpen(true)
    setPiAuthInput('')
    void piAuth.onLogin()
  }

  const openPiAuth = () => {
    if (!piAuth) return
    setIsAgentRegisterOpen(false)
    setIsPiAuthOpen(true)
    const status = piAuth.attempt?.status
    if (!piAuth.isAuthenticated && !piAuth.isBusy && (!status || status === 'failed')) {
      startPiLogin()
    }
  }

  const submitPiAuthInput = async (event: FormEvent) => {
    event.preventDefault()
    const value = piAuthInput.trim()
    if (!piAuth?.onSubmitInput || !value || piAuth.isBusy || isPiAuthSubmitting) return
    setIsPiAuthSubmitting(true)
    try {
      await piAuth.onSubmitInput(value)
      setPiAuthInput('')
    } finally {
      setIsPiAuthSubmitting(false)
    }
  }

  const piAuthStatusLabel = piAuth?.isAuthenticated
    ? 'ChatGPT connected'
    : piAuth?.isBusy
      ? 'connecting to ChatGPT'
      : piAuth?.error
        ? 'Pi service unavailable'
        : piAuth?.attempt?.status === 'failed'
        ? 'login failed'
        : 'login with ChatGPT'
  const piAuthTriggerLabel = piAuth?.isAuthenticated
    ? 'pi connected'
    : piAuth?.isBusy
      ? 'pi connecting'
      : piAuth?.error
        ? 'pi unavailable'
        : piAuth?.attempt?.status === 'failed'
        ? 'pi login failed'
        : 'pi login'

  const currentAgent = piAgent?.current || piAgent?.report?.current || null
  const currentModelLabel = currentAgent?.model?.name || currentAgent?.model?.modelId || piAgent?.configuredModel.modelId || 'model not selected'
  const currentThinkingLabel = currentAgent?.thinkingLevel || piAgent?.configuredThinkingLevel || 'off'
  const selectedAgentModel = piAgent?.models.find(model => model.modelId === selectedAgentModelId)
  const selectableThinkingLevels = selectedAgentModel?.thinkingLevels || currentAgent?.availableThinkingLevels || ['off']

  const openAgentRegister = () => {
    if (!piAgent) return
    setIsPiAuthOpen(false)
    setAgentRegisterTab('session')
    setIsAgentRegisterOpen(true)
    void piAgent.onRefresh()
  }

  const openInquiryRegister = () => {
    if (!chapterId) return
    setIsPiAuthOpen(false)
    setAgentRegisterTab('inquiries')
    setIsAgentRegisterOpen(true)
  }

  const startConfiguredAgent = () => {
    if (!piAgent || !selectedAgentModel || piAgent.isBusy) return
    void piAgent.onStart({
      model: {
        provider: selectedAgentModel.provider,
        modelId: selectedAgentModel.modelId,
      },
      thinkingLevel: selectedThinkingLevel,
    })
  }

  const resumeAgentSession = (agentSessionId: string) => {
    if (!piAgent || piAgent.isBusy) return
    void piAgent.onStart({ resumeAgentSessionId: agentSessionId })
  }

  const inquiryCoordinateStatus = (inquiry: CameraLucidaPersistedInquiry): string => {
    if (!inquiry.findings.length) return 'report'
    if (!inquiry.findings.every(findingCoordinatesResolve)) return 'report only'
    return inquiry.snapshotId === snapshotId ? 'current collation' : 'coordinates resolve'
  }

  return (
    <div
      className="camera-lucida"
      data-testid="agent-camera-lucida"
      ref={rootRef}
      style={{ '--cl-witness-count': model.witnesses.length } as CSSProperties}
    >
      <header className="masthead">
        <div className="wordmark">Variorum <em>camera lucida</em></div>
        <div className="state">
          <b>{contextLabel}</b><span className="sep">·</span><b>{model.rows.length} rows</b><span className="sep">·</span><b>{model.witnesses.length} witnesses</b>
          <span className="sep">·</span><span>plate {roman(selectedLeaf + 1)} of {roman(Math.max(1, model.leaves.length))}</span><span className="sep">·</span>
          <span className="statuschip"><span className={classNames('statusdot', (statusLive || agentOccupied) && 'live')} /><span>{statusLabel}</span></span>
        </div>
      </header>

      <main className={classNames('plate', reentryFinding && 'recede')} ref={plateRef}>
        <div className={classNames('plateInner', session.overlay && 'chars')}>
          <div className={classNames('withead', focused && 'focused')} style={{ transform: `translateY(${layout.lift}px)` }}>
            {model.witnesses.map(witness => (
              <div className={classNames('wcol', shownScope && scopeWitnessIds.has(witness.id) && 'on')} key={witness.id} title={[witness.siglum, witness.secondaryLabel].filter(Boolean).join(' · ')}>
                <div className="sig">{witness.siglum}</div>
              </div>
            ))}
          </div>
          <div className="stack" ref={stackRef}>
            {model.leaves.map((leaf, leafIndex) => {
              const visible = layout.window.includes(leafIndex)
              const selected = leafIndex === selectedLeaf
              const dimField = focused && selected
              let rowOffset = 8
              return (
                <div
                  className={classNames(
                    'leafgroup',
                    risingLeaf === leafIndex && 'noanim',
                    leaf.depth > 0 && 'nested',
                    `kind-${leaf.kind}`,
                    selected && 'sel',
                    visible && !selected && 'side',
                    !visible && 'stub',
                    dimField && 'dimfield',
                  )}
                  key={leaf.id}
                  ref={element => { if (element) leafRefs.current.set(leafIndex, element); else leafRefs.current.delete(leafIndex) }}
                  style={{
                    ...pageStyles(leafIndex),
                    '--cl-structure-depth': Math.min(4, Math.max(0, leaf.depth)),
                  } as CSSProperties}
                  data-section-kind={leaf.kind}
                  data-section-source={leaf.source}
                  title={`${roman(leafIndex + 1)} · ${leaf.title} · ${sectionKindLabel(leaf.kind)}`}
                  onClick={() => selectLeaf(leafIndex)}
                  onTransitionEnd={event => {
                    if (event.target === event.currentTarget && event.propertyName === 'transform') {
                      finishLeafTransition(leafIndex)
                    }
                  }}
                >
                  <div className="stubface">
                    <span className="fnum">{roman(leafIndex + 1)}</span>
                    <span className="fname">{sectionKindLabel(leaf.kind)} · {leaf.title}</span>
                    <span className="frows">{leaf.rows.length} rows</span>
                  </div>
                  {visible && <>
                    <div className="scenetitle">
                      <span className="tnum">{roman(leafIndex + 1)}</span>
                      <span className="tcopy">
                        {leaf.structurePath.length > 1 ? (
                          <span className="tpath">
                            {leaf.structurePath.slice(0, -1).map(entry => entry.title).join(' / ')}
                          </span>
                        ) : null}
                        <span className="tname">{leaf.title}{leaf.splitCount > 1 && leaf.splitIndex > 0 ? ' (cont.)' : ''}</span>
                      </span>
                      <span className="tmeta">{sectionKindLabel(leaf.kind)} · {leaf.rows.length} units · rows {leaf.rows[0]?.ordinal || 0}–{leaf.rows[leaf.rows.length - 1]?.ordinal || 0}</span>
                    </div>
                    <div className="leafrow">
                    <div className="gutter">
                      {leaf.rows.map(row => {
                        const height = Math.max(4, Math.round(row.height * plateScale))
                        return (
                          <div
                            className={classNames('grow', session.coverageRowIds.includes(row.id) && 'covered', scopeRowIds.has(row.id) && 'rowon')}
                            key={row.id}
                            style={{ height, transitionDelay: session.coverageRowIds.includes(row.id) ? `${140 + row.ordinal * 55}ms` : undefined }}
                          >
                            <span className="trailtick" />{row.ordinal}
                          </div>
                        )
                      })}
                    </div>
                    <div className="leaf">
                      {leaf.rows.map((row, rowIndex) => {
                        const height = Math.max(4, Math.round(row.height * plateScale))
                        return (
                          <div
                            className="row"
                            key={row.id}
                            ref={element => { if (element) rowRefs.current.set(row.id, element); else rowRefs.current.delete(row.id) }}
                            style={{ height }}
                          >
                            <div className="cells">
                              {row.cells.map(cell => {
                                const lit = focused && scopeRowIds.has(row.id) && scopeWitnessIds.has(cell.witnessId)
                                return (
                                  <div
                                    className={classNames('cell', lit && 'lit')}
                                    key={cell.witnessId}
                                    ref={element => {
                                      const key = `${row.id}:${cell.witnessId}`
                                      if (element) cellRefs.current.set(key, element); else cellRefs.current.delete(key)
                                    }}
                                    style={{
                                      background: cellBackground(cell.state, cell.fill),
                                      transitionDelay: lit ? `${170 + rowIndex * 16}ms` : '0ms',
                                    }}
                                  />
                                )
                              })}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div className="charmargin">
                      <span className="marginlabel">character layer</span>
                      {leaf.rows.map((row, rowIndex) => {
                        const height = Math.max(4, Math.round(row.height * plateScale))
                        const top = rowOffset
                        rowOffset += height + 2
                        const characterIds = Array.from(new Set(presenceCharacterIdsByRowId.get(row.id) || []))
                        if (!characterIds.length) return null
                        return (
                          <span className="prow" key={`presence-${row.id}`} style={{ top, height, '--d': `${rowIndex * 22}ms` } as CSSProperties}>
                            {characterIds.map(characterId => {
                              const character = characterById.get(characterId)
                              return character ? <i key={characterId} style={{ background: character.color }} title={character.name} /> : null
                            })}
                          </span>
                        )
                      })}
                      {(speakerSpansByLeafIndex.get(leafIndex) || []).map((span, index) => {
                        const spanRows = span.rowIds.map(rowId => leaf.rows.find(row => row.id === rowId)).filter((row): row is NonNullable<typeof row> => Boolean(row))
                        if (!spanRows.length) return null
                        const firstIndex = leaf.rows.findIndex(row => row.id === spanRows[0].id)
                        const top = 8 + leaf.rows.slice(0, firstIndex).reduce((sum, row) => sum + Math.max(4, Math.round(row.height * plateScale)) + 2, 0)
                        const height = spanRows.reduce((sum, row) => sum + Math.max(4, Math.round(row.height * plateScale)) + 2, 0) - 2
                        const character = characterById.get(span.characterId)
                        return character ? (
                          <React.Fragment key={span.id}>
                            <span className="spkbar" style={{ top, height, background: character.color, '--d': `${index * 55}ms` } as CSSProperties} />
                            <span className="spkname" style={{ top, color: character.color, '--d': `${index * 55}ms` } as CSSProperties}>{character.name}</span>
                          </React.Fragment>
                        ) : null
                      })}
                      {session.overlay && session.overlay.rowIds.some(rowId => leaf.rows.some(row => row.id === rowId)) && (() => {
                        const indices = session.overlay.rowIds.map(rowId => leaf.rows.findIndex(row => row.id === rowId)).filter(index => index >= 0).sort((a, b) => a - b)
                        if (!indices.length) return null
                        const first = indices[0]
                        const last = indices[indices.length - 1]
                        const top = 6 + leaf.rows.slice(0, first).reduce((sum, row) => sum + Math.max(4, Math.round(row.height * plateScale)) + 2, 0)
                        const height = leaf.rows.slice(first, last + 1).reduce((sum, row) => sum + Math.max(4, Math.round(row.height * plateScale)) + 2, 0) + 2
                        return <span className="probespan probing" style={{ top, height }} />
                      })()}
                    </div>
                  </div>
                  </>}
                </div>
              )
            })}
          </div>
          <div className={classNames('matteLayer', matteBlink && 'blink', (session.status === 'reading' || session.status === 'inspecting') && !citedFinding && !reentryFinding && 'reading')} ref={matteLayerRef}>
            {liveMattes.map((rect, index) => <div className={classNames('matte live', !rect && 'off')} key={index} style={rect || undefined} />)}
            <div className={classNames('matte suggest', !suggestMatte && 'off')} style={suggestMatte || undefined} />
            <div className={classNames('matte citering', !citeMatte && 'off')} style={citeMatte || undefined} />
          </div>
          <div className="mapcap" ref={capRef} style={{ top: (stackRef.current?.offsetTop || 40) + layout.capTop }}>
            <div className="l1"><span className={classNames('captick', (session.status === 'inspecting' || session.status === 'reading' || session.status === 'consulting' || session.status === 'filing' || citedFinding) && 'live')} /><span id="capMain" className="capswap">{activityMain}</span></div>
            <div id="capUnits">{activityUnits}</div>
          </div>
          <div className={classNames('overlaytag', session.overlay && 'open')} style={{ top: 4 + layout.lift }}>
            <span>the agent consults</span>
            <span><b>Character Index</b>{overlayCharacter && <> · <span className="qar">{overlayCharacter.name}</span></>}</span>
            <span className="step">{session.overlay ? 'reading the index' : ''}</span>
          </div>
          <div
            className={classNames('ledger', reentryFinding && 'open')}
            ref={ledgerRef}
            style={{
              transformOrigin: reentryFinding
                ? `${(() => {
                  const columns = reentryFinding.scope.witnessIds.map(id => allWitnessIds.indexOf(id)).filter(index => index >= 0)
                  return columns.length ? ((Math.min(...columns) + Math.max(...columns) + 1) / 2 / Math.max(1, allWitnessIds.length) * 100) : 50
                })()}% 42%`
                : '50% 42%',
            }}
          >
            {reentryFinding && <>
              <div className="lhead">
                <span>{reentryFinding.suggested ? 'SUGGESTED READING' : 'LEDGER'} · <b>ROWS {rowRangeLabel(reentryFinding.scope)}</b> · {(() => {
                  const plate = findingPlate(reentryFinding)
                  return plate.index === plate.lastIndex ? plate.title.toUpperCase() : `PLATES ${plateNumerals(plate)}`
                })()}</span>
                <span>the agent read here · <kbd>Esc</kbd> returns to the plate</span>
              </div>
              <div className="ledgrid">
                <div className="lg-corner" />
                {model.witnesses.map(witness => {
                  const on = reentryFinding.scope.witnessIds.includes(witness.id)
                  return <div className={classNames('lg-sig', on ? 'onw' : 'dimw')} key={`head-${witness.id}`}>{witness.siglum}<small>{witness.secondaryLabel || ''}</small></div>
                })}
                {reentryFinding.scope.rowIds.map(rowId => rowById.get(rowId)).filter((row): row is NonNullable<typeof row> => Boolean(row)).sort((left, right) => left.ordinal - right.ordinal).map(row => <React.Fragment key={`ledger-${row.id}`}>
                  <div className="lg-rowhead"><span className="lrn">Nº {row.ordinal}</span>{row.title && <span className="lrt">{row.title}</span>}</div>
                  {row.cells.map(cell => {
                    const on = reentryFinding.scope.witnessIds.includes(cell.witnessId)
                    if (cell.state === 'lacuna') return <div className={classNames('lg-cell', 'laccell', !on && 'dimw')} key={cell.witnessId}><span className="lacnote">⟨ lacuna ⟩</span></div>
                    return <div className={classNames('lg-cell', !on && 'dimw')} dir="rtl" key={cell.witnessId}>{cell.text}</div>
                  })}
                </React.Fragment>)}
              </div>
            </>}
          </div>
        </div>
        <aside className="reportCol" ref={reportRef} style={{ paddingTop: layout.lift }} onScroll={drawLeaders}><FindingCards report={wideReport} showProposal={wideReport} /></aside>
        <svg className="leaders" aria-hidden="true">
          {leaderPaths.map(path => <path d={path.path} fill="none" key={path.id} stroke={path.active ? '#2563eb' : 'rgba(24,21,17,.32)'} strokeWidth={path.active ? 1.5 : .75} />)}
        </svg>
      </main>

      <div className="exchange">
        <div className="inner">
          <div className="lanes">
            <div className="discourse" ref={discourseRef}>
              {session.exchange.filter(entry => entry.kind !== 'summary' || !wideReport).map(entry => (
                <div className={classNames('entry', entry.kind === 'question' && 'q', entry.kind === 'summary' && 'sum')} key={entry.id}>
                  {entry.kind !== 'question' && entry.kind !== 'summary' && <span className={classNames('ptag', (entry.kind === 'filed' || entry.kind === 'suggested') && 'filed')}>{entry.kind === 'remark' ? 'agent' : entry.kind}</span>}
                  <span className="txt"><RichText text={entry.text} scholarAuthored={entry.role === 'scholar'} /></span>
                </div>
              ))}
              <div className="tail capswap">{identityPresentation.safeAgentText(session.live)}</div>
            </div>
            <aside className="docket"><FindingCards showProposal={!wideReport} /></aside>
          </div>
          <form className="askbar" onSubmit={onAskSubmit}>
            <span className="prompt">ask ›</span>
            <input
              ref={inputRef}
              autoComplete="off"
              spellCheck={false}
              placeholder={DEFAULT_QUESTION}
              value={question}
              onChange={event => setQuestion(event.target.value)}
              onKeyDown={onAskKeyDown}
            />
          </form>
          {piAuth && isPiAuthOpen && (
            <section className="piauth" aria-label="Pi login" aria-live="polite">
              <div className="piauth-kicker">
                <span>pi access</span>
                <span className={classNames('piauth-dot', piAuth.isAuthenticated && 'connected', piAuth.isBusy && 'busy', (piAuth.attempt?.status === 'failed' || piAuth.error) && 'failed')} />
              </div>
              <div className="piauth-copy">
                <div className="piauth-status">
                  <b>{piAuth.attempt?.providerName || 'ChatGPT'}</b>
                  <span>·</span>
                  <span>{piAuthStatusLabel}</span>
                </div>
                {piAuth.attempt?.instructions && !piAuth.isAuthenticated && (
                  <p>{piAuth.attempt.instructions}</p>
                )}
                {piAuth.attempt?.authUrl && !piAuth.isAuthenticated && (
                  <a href={piAuth.attempt.authUrl} target="_blank" rel="noreferrer">open login window ↗</a>
                )}
                {piAuth.attempt?.manualCodeSupported && !piAuth.isAuthenticated && piAuth.attempt.status !== 'failed' && (
                  <form className="piauth-manual" onSubmit={submitPiAuthInput}>
                    <input
                      aria-label="Paste callback URL or authorization code"
                      autoComplete="off"
                      spellCheck={false}
                      value={piAuthInput}
                      onChange={event => setPiAuthInput(event.target.value)}
                      onKeyDown={event => event.stopPropagation()}
                      placeholder={piAuth.attempt.prompt || 'Paste callback URL or authorization code'}
                    />
                    <button type="submit" disabled={!piAuthInput.trim() || piAuth.isBusy || isPiAuthSubmitting}>submit</button>
                  </form>
                )}
                {piAuth.attempt?.status === 'failed' && piAuth.attempt.error && <p className="piauth-error">{identityPresentation.safeAgentText(piAuth.attempt.error)}</p>}
                {piAuth.error && piAuth.error !== piAuth.attempt?.error && <p className="piauth-error">{identityPresentation.safeAgentText(piAuth.error)}</p>}
                {piAuth.isAuthenticated && <p>Pi is authenticated and ready to read this collation.</p>}
              </div>
              <div className="piauth-actions">
                {(piAuth.isAuthenticated || piAuth.attempt?.status === 'failed') && (
                  <button type="button" onClick={startPiLogin} disabled={piAuth.isBusy} onKeyDown={event => event.stopPropagation()}>
                    {piAuth.isAuthenticated ? 're-auth' : 'try again'}
                  </button>
                )}
                <button type="button" onClick={() => setIsPiAuthOpen(false)} onKeyDown={event => event.stopPropagation()}>close</button>
              </div>
            </section>
          )}
          {(piAgent || chapterId) && isAgentRegisterOpen && (
            <section
              className="agentregister"
              aria-label={agentRegisterTab === 'inquiries' ? 'Camera Lucida inquiry register' : 'Agent session register'}
              aria-live="polite"
              onKeyDown={event => event.stopPropagation()}
            >
              <div className="agentregister-kicker">
                <span>{agentRegisterTab === 'inquiries' ? 'inquiry register' : 'agent register'}</span>
                <span className={classNames(
                  'agentregister-dot',
                  (agentRegisterTab === 'inquiries' ? persistedInquiries.length > 0 : currentAgent) && 'connected',
                  (agentRegisterTab === 'inquiries' ? inquiryHistoryLoading : piAgent?.isBusy) && 'busy',
                  (agentRegisterTab === 'inquiries' ? inquiryHistoryError : piAgent?.error) && 'failed',
                )} />
              </div>
              <div className="agentregister-main">
                <div className="agentregister-status">
                  {agentRegisterTab === 'inquiries' ? <>
                    <span>completed inquiries</span>
                    <b>{contextLabel}</b>
                    <span>· {persistedInquiries.length} saved {persistedInquiries.length === 1 ? 'report' : 'reports'}</span>
                    {restoredInquiry && <span>· archived report open</span>}
                  </> : <>
                    <span>{currentAgent ? 'current session' : 'session not started'}</span>
                    <b>{currentModelLabel}</b>
                    <span>· {currentThinkingLabel} effort</span>
                    {currentAgent && <span>· {formatTokenCount(currentAgent.usage.total)} used tokens</span>}
                  </>}
                </div>
                <nav className="agentregister-tabs" aria-label="Agent register sections">
                  {([
                    ...(chapterId ? ['inquiries'] as AgentRegisterTab[] : []),
                    ...(piAgent ? ['session', 'agent-history', 'tools'] as AgentRegisterTab[] : []),
                  ] as AgentRegisterTab[]).map(tab => (
                    <button
                      type="button"
                      className={agentRegisterTab === tab ? 'active' : ''}
                      key={tab}
                      onClick={() => setAgentRegisterTab(tab)}
                    >
                      {tab === 'agent-history' ? 'agent history' : tab}
                      {tab === 'inquiries' ? ` ${persistedInquiries.length}` : ''}
                      {tab === 'agent-history' && piAgent?.report ? ` ${piAgent.report.history.length}` : ''}
                    </button>
                  ))}
                </nav>

                {agentRegisterTab === 'inquiries' && (
                  <div className="agentregister-history inquiry-history" aria-label="Saved Camera Lucida inquiries">
                    {inquiryHistoryLoading && !persistedInquiries.length && <p className="agentregister-empty">Loading saved inquiries…</p>}
                    {!inquiryHistoryLoading && !inquiryHistoryError && !persistedInquiries.length && <p className="agentregister-empty">No completed Camera Lucida inquiries have been saved for this chapter yet.</p>}
                    {inquiryHistoryError && <p className="agentregister-error">{identityPresentation.safeAgentText(inquiryHistoryError)}</p>}
                    {persistedInquiries.map(inquiry => {
                      const coordinateStatus = inquiryCoordinateStatus(inquiry)
                      const isOpen = restoredInquiry?.id === inquiry.id
                      return (
                        <button
                          type="button"
                          className={classNames('agentregister-history-row', isOpen && 'current')}
                          key={inquiry.id}
                          onClick={() => inspectPersistedInquiry(inquiry)}
                          disabled={Boolean(piAgent?.isBusy || !['standing by', 'idle', 'error'].includes(session.status))}
                        >
                          <span className="agentregister-history-title">{inquiry.question || 'Untitled inquiry'}</span>
                          <span>{formatSessionDate(inquiry.completedAt)} · {inquiry.findings.length} {inquiry.findings.length === 1 ? 'finding' : 'findings'}</span>
                          <span>{inquiry.model?.modelId || 'model unknown'}{inquiry.thinkingLevel ? ` · ${inquiry.thinkingLevel} effort` : ''}</span>
                          <i>{isOpen ? 'open' : `${coordinateStatus} ↗`}</i>
                        </button>
                      )
                    })}
                  </div>
                )}

                {piAgent && agentRegisterTab === 'session' && (
                  <div className="agentregister-session">
                    <div className="agentregister-fields">
                      <label>
                        <span>OpenAI model</span>
                        <select
                          aria-label="OpenAI model"
                          value={selectedAgentModelId}
                          onChange={event => {
                            const modelId = event.target.value
                            const option = piAgent.models.find(model => model.modelId === modelId)
                            setSelectedAgentModelId(modelId)
                            if (option && !option.thinkingLevels.includes(selectedThinkingLevel)) {
                              setSelectedThinkingLevel(option.thinkingLevels.includes('medium') ? 'medium' : option.thinkingLevels[0] || 'off')
                            }
                          }}
                          disabled={piAgent.isBusy || !piAgent.models.length}
                        >
                          {!piAgent.models.length && <option value="">refreshing model registry…</option>}
                          {piAgent.models.map(modelOption => (
                            <option value={modelOption.modelId} key={`${modelOption.provider}:${modelOption.modelId}`}>
                              {modelOption.name}{modelOption.available ? '' : ' · login required'}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>thinking effort</span>
                        <select
                          aria-label="Thinking effort"
                          value={selectedThinkingLevel}
                          onChange={event => setSelectedThinkingLevel(event.target.value as PiMatrixAgentThinkingLevel)}
                          disabled={piAgent.isBusy || !selectedAgentModel}
                        >
                          {selectableThinkingLevels.map(level => <option value={level} key={level}>{level}</option>)}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="agentregister-primary"
                        disabled={piAgent.isBusy || !selectedAgentModel?.available}
                        onClick={startConfiguredAgent}
                      >
                        {piAgent.isBusy ? 'opening…' : currentAgent ? 'start new' : 'start session'}
                      </button>
                    </div>
                    <div className="agentregister-metrics">
                      <span><b>{formatTokenCount(currentAgent?.usage.input)}</b> input</span>
                      <span><b>{formatTokenCount(currentAgent?.usage.output)}</b> output</span>
                      <span><b>{formatTokenCount((currentAgent?.usage.cacheRead || 0) + (currentAgent?.usage.cacheWrite || 0))}</b> cache</span>
                      <span><b>{currentAgent?.contextUsage?.tokens === null ? '?' : formatTokenCount(currentAgent?.contextUsage?.tokens)}</b> / {formatTokenCount(currentAgent?.contextUsage?.contextWindow)} context</span>
                      <span><b>{currentAgent?.compaction?.enabled ? 'on' : 'off'}</b> compaction</span>
                    </div>
                    {!selectedAgentModel?.available && selectedAgentModel && <p className="agentregister-note">Connect ChatGPT before starting this model. The current runtime identity above remains unchanged until a new session opens.</p>}
                    {piAgent.error && <p className="agentregister-error">{identityPresentation.safeAgentText(piAgent.error)}</p>}
                  </div>
                )}

                {piAgent && agentRegisterTab === 'agent-history' && (
                  <div className="agentregister-history">
                    {!piAgent.report?.history.length && <p className="agentregister-empty">No persistent Pi sessions have been recorded for this workspace yet.</p>}
                    {piAgent.report?.history.map(historySession => (
                      <button
                        type="button"
                        className={classNames(
                          'agentregister-history-row',
                          currentAgent?.agentSessionId === historySession.agentSessionId && 'current',
                          !historySession.resumeCompatible && 'incompatible',
                        )}
                        key={historySession.agentSessionId}
                        onClick={() => resumeAgentSession(historySession.agentSessionId)}
                        disabled={piAgent.isBusy || currentAgent?.agentSessionId === historySession.agentSessionId || !historySession.resumeCompatible}
                        title={historySession.resumeBlockedReason || undefined}
                      >
                        <span className="agentregister-history-title">{historySession.name || historySession.firstMessage || 'Untitled agent session'}</span>
                        <span>{formatSessionDate(historySession.modifiedAt)} · {historySession.messageCount} messages</span>
                        <span>{historySession.model?.modelId || 'model unknown'} · {historySession.thinkingLevel} · {formatTokenCount(historySession.usage.total)} tokens</span>
                        <i>{currentAgent?.agentSessionId === historySession.agentSessionId ? 'current' : historySession.resumeCompatible ? 'resume ↗' : 'snapshot changed'}</i>
                      </button>
                    ))}
                  </div>
                )}

                {piAgent && agentRegisterTab === 'tools' && (
                  <div className="agentregister-tools">
                    {piAgent.report?.tools ? (
                      <>
                        <p className="agentregister-tool-summary">
                          <b>{piAgent.report.tools.toolCount} tools</b> · ≈ {formatTokenCount(piAgent.report.tools.estimatedTokens)} context tokens · {piAgent.report.tools.estimationMethod}
                        </p>
                        <div className="agentregister-tool-list">
                          {piAgent.report.tools.tools.map(tool => (
                            <div className="agentregister-tool-row" key={tool.name}>
                              <span className={`toolgroup toolgroup-${tool.group}`}>{tool.group}</span>
                              <b>{tool.name}</b>
                              <span>{tool.description}</span>
                              <i>≈ {formatTokenCount(tool.estimatedTokens)}</i>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <p className="agentregister-empty">Refresh the register to calculate the active tool footprint.</p>}
                  </div>
                )}
              </div>
              <div className="agentregister-actions">
                <button
                  type="button"
                  onClick={() => agentRegisterTab === 'inquiries' ? void refreshInquiryHistory() : void piAgent?.onRefresh()}
                  disabled={agentRegisterTab === 'inquiries' ? inquiryHistoryLoading : piAgent?.isBusy}
                >refresh</button>
                <button type="button" onClick={() => setIsAgentRegisterOpen(false)}>close</button>
              </div>
            </section>
          )}
        </div>
      </div>

      <footer className="console">
        <span>
          agent session · <span className="ok">lens live</span>
          {piAgent && <>{' · '}<button
            type="button"
            aria-label={`Agent session: ${currentModelLabel}, ${currentThinkingLabel} thinking effort`}
            className={classNames('agentregister-trigger', piAgent.isBusy && 'busy', piAgent.error && 'failed')}
            onClick={openAgentRegister}
            onKeyDown={event => event.stopPropagation()}
          >{currentModelLabel} · {currentThinkingLabel}</button></>}
          {chapterId && <>{' · '}<button
            type="button"
            aria-label={`Saved Camera Lucida inquiries: ${persistedInquiries.length}`}
            className={classNames('agentregister-trigger', inquiryHistoryLoading && 'busy', inquiryHistoryError && 'failed')}
            onClick={openInquiryRegister}
            onKeyDown={event => event.stopPropagation()}
          >{inquiryHistoryLoading && !persistedInquiries.length ? 'inquiries …' : `inquiries ${persistedInquiries.length}`}</button></>}
          {piAuth && <>{' · '}<button
            type="button"
            aria-label={piAuthStatusLabel}
            className={classNames('piauth-trigger', piAuth.isAuthenticated && 'connected', piAuth.isBusy && 'busy', (piAuth.attempt?.status === 'failed' || piAuth.error) && 'failed')}
            onClick={openPiAuth}
            onKeyDown={event => event.stopPropagation()}
          >{piAuthTriggerLabel}</button></>}
        </span>
        <div className="legend">
          <span><i style={{ background: '#0f172a' }} />text mass</span>
          <span><i style={{ background: 'linear-gradient(to bottom,#0f172a 0 55%,#fffdfa 55% 100%)' }} />shorter unit</span>
          <span><i style={{ background: 'repeating-linear-gradient(135deg,#fffdfa 0,#fffdfa 2px,#ede8de 2px,#ede8de 4px)' }} />lacuna</span>
          <span><i style={{ background: '#ede8de' }} />not carried</span>
          <span><i style={{ border: '1.5px solid #2563eb', background: 'rgba(255,255,255,.2)' }} />agent reads</span>
          <span><i style={{ border: '2px solid #2563eb' }} />cited</span>
          <span><i style={{ border: '1.5px dashed #2563eb' }} />suggested</span>
          <span><i style={{ background: '#047857' }} />trail</span>
        </div>
        <span><kbd>⏎</kbd> ask / re-enter <kbd>space</kbd> pause <kbd>←</kbd><kbd>→</kbd> plates <kbd>1</kbd>–<kbd>4</kbd> cite <kbd>esc</kbd> back <kbd>r</kbd> replay</span>
      </footer>
    </div>
  )
}

export default AgentCameraLucidaView
